import {
	CUSTOM_STYLE_MAX_LENGTH,
	DESIGNER_BIO_MAX_LENGTH,
	type DesignerProjectType,
	type OnboardingPrefill,
} from "@shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	MultiImageUpload,
	SingleImageUpload,
} from "@/components/ui/image-upload";
import { StepProgress } from "@/components/ui/step-progress";
import { SuccessScreen } from "@/components/ui/success-screen";
import { createDesignerProfile } from "@/features/onboarding/services/onboarding-api.service";
import {
	FieldInput,
	FieldSelect,
	FloatPanel,
	StudioButton,
} from "@/features/studio/theme";
import { useServiceCities } from "@/lib/constants/markets";
import { titleCase } from "@/lib/format";
import {
	fillLocationIfEmpty,
	keepOrFill,
	useApplyPrefillOnce,
} from "../hooks/use-onboarding-prefill";
import {
	designerProjectTypeOptions,
	designerServiceOptions,
	designStyleOptions,
} from "../options";
import { BackLink } from "./back-link";
import {
	BudgetRangeFields,
	budgetErrorKey,
	budgetPayload,
	CharCountTextarea,
	CityChipsPicker,
	EMPTY_BUDGET,
	EMPTY_LOCATION,
	FieldGroupLabel,
	LocationFields,
	locationErrorKey,
	locationPayload,
	OptionChips,
} from "./fields";

type Step = "basic" | "expertise" | "showcase" | "complete";
const STEPS: Step[] = ["basic", "expertise", "showcase", "complete"];
const PANEL_PADDING = "clamp(24px, 6vw, 40px) clamp(20px, 5vw, 32px)";

export interface DesignerOnboardingFlowProps {
	/** Back from the first step. Without it the first step has no Back link. */
	onExit?: () => void;
	/**
	 * Rendered above the first step only, where the Back link to the role
	 * picker goes. The onboarding page passes the role-list retry here; the
	 * way back to another role goes in `backToRole`.
	 */
	exitControl?: React.ReactNode;
	/**
	 * The first step's way back to the role the account already holds,
	 * placed beside Continue: the slot later steps use for their Back.
	 */
	backToRole?: React.ReactNode;
	/** Details the account already gave; fills empty fields once. */
	prefill?: OnboardingPrefill;
	/** The profile was created; the flow shows its success screen next. */
	onProfileCreated: () => void;
	/** The server already holds a finished profile (409). */
	onAlreadyExists: () => void;
	/** The success screen's button. */
	onFinished: () => void;
}

/**
 * Designer onboarding: basic info, expertise, then an optional showcase.
 * Nothing is saved until the last step, which can be skipped.
 */
export function DesignerOnboardingFlow({
	onExit,
	exitControl,
	backToRole,
	prefill,
	onProfileCreated,
	onAlreadyExists,
	onFinished,
}: DesignerOnboardingFlowProps) {
	const { t } = useTranslation();
	const cityOptions = useServiceCities().map((city) => city.value);
	const [step, setStep] = useState<Step>("basic");
	const [isLoading, setIsLoading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	// Basic info
	const [photoUrl, setPhotoUrl] = useState("");
	const [name, setName] = useState("");
	const [firmName, setFirmName] = useState("");
	const [experienceYears, setExperienceYears] = useState("");
	const [location, setLocation] = useState(EMPTY_LOCATION);

	useApplyPrefillOnce(prefill, (known) => {
		setName((current) => keepOrFill(current, known.name));
		setPhotoUrl((current) => keepOrFill(current, known.profilePictureUrl));
		setLocation((current) => fillLocationIfEmpty(current, known));
	});
	const [bio, setBio] = useState("");

	// Expertise
	const [cities, setCities] = useState<string[]>([]);
	const [styles, setStyles] = useState<string[]>([]);
	const [customStyle, setCustomStyle] = useState("");
	const [services, setServices] = useState<string[]>([]);
	const [projectTypes, setProjectTypes] = useState<DesignerProjectType[]>([]);
	const [projectTypesOther, setProjectTypesOther] = useState("");
	const [budget, setBudget] = useState(EMPTY_BUDGET);

	// Showcase
	const [portfolioImages, setPortfolioImages] = useState<string[]>([]);

	const stepNames: Record<Step, string> = {
		basic: t("onboarding.designer.steps.basicInfo"),
		expertise: t("onboarding.designer.steps.expertise"),
		showcase: t("onboarding.designer.steps.showcase"),
		complete: t("onboarding.designer.steps.complete"),
	};

	const addCustomStyle = () => {
		const style = titleCase(customStyle).slice(0, CUSTOM_STYLE_MAX_LENGTH);
		if (!style) return;
		const existing = designStyleOptions(t, styles).find(
			(option) => option.value.toLowerCase() === style.toLowerCase(),
		);
		const value = existing?.value ?? style;
		setStyles((prev) => (prev.includes(value) ? prev : [...prev, value]));
		setCustomStyle("");
	};

	const handleBasicSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!name.trim()) {
			toast.error(t("onboarding.designer.pleaseEnterName"));
			return;
		}
		const locationError = locationErrorKey(location);
		if (locationError) {
			toast.error(t(locationError));
			return;
		}
		setStep("expertise");
	};

	const handleExpertiseSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (cities.length === 0) {
			toast.error(t("onboarding.designer.selectAtLeastOneCity"));
			return;
		}
		if (styles.length === 0) {
			toast.error(t("onboarding.designer.selectAtLeastOneStyle"));
			return;
		}
		const budgetError = budgetErrorKey(budget);
		if (budgetError) {
			toast.error(t(budgetError));
			return;
		}
		setStep("showcase");
	};

	const submit = async (images: string[]) => {
		const { priceRangeMin, priceRangeMax } = budgetPayload(budget);

		setIsLoading(true);
		try {
			const result = await createDesignerProfile({
				name: name.trim(),
				firmName: firmName.trim() || undefined,
				bio: bio.trim() || undefined,
				profilePictureUrl: photoUrl || undefined,
				experienceYears: experienceYears
					? Number.parseInt(experienceYears, 10)
					: undefined,
				...locationPayload(location),
				serviceCities: cities,
				styles,
				services,
				projectTypes,
				projectTypesOther: projectTypes.includes("other")
					? projectTypesOther.trim() || null
					: null,
				priceRangeMin: priceRangeMin ?? undefined,
				priceRangeMax: priceRangeMax ?? undefined,
				portfolioImages: images,
			});

			if (result.success) {
				onProfileCreated();
				toast.success(t("onboarding.designer.profileCreated"));
				setStep("complete");
			} else if (result.alreadyExists) {
				toast.info(t("onboarding.designer.profileAlreadyExists"));
				onAlreadyExists();
			} else {
				toast.error(
					result.error || t("onboarding.designer.failedToCreateProfile"),
				);
			}
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsLoading(false);
		}
	};

	const progress = (
		<StepProgress
			steps={STEPS.map((s) => stepNames[s])}
			currentStep={stepNames[step]}
			className="mb-8"
		/>
	);

	if (step === "complete") {
		return (
			<>
				{progress}
				<SuccessScreen
					title={t("onboarding.designer.completeTitle")}
					description={t("onboarding.designer.completeSubtitle")}
					actionLabel={t("common.goToDashboard")}
					onAction={onFinished}
					notice={{
						title: t("onboarding.designer.verificationPendingTitle"),
						message: t("onboarding.designer.verificationPendingMsg"),
					}}
				/>
			</>
		);
	}

	return (
		<>
			{step === "basic" && exitControl}
			{step === "basic" && onExit && <BackLink onClick={onExit} />}
			{progress}

			{step === "basic" && (
				<FloatPanel elevation={2} padding={PANEL_PADDING}>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.designer.basicStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.designer.basicStepSubtitle")}
					</p>

					<form onSubmit={handleBasicSubmit} className="space-y-5">
						<div>
							<FieldGroupLabel>
								{t("onboarding.fields.profilePhotoOrLogo")}
							</FieldGroupLabel>
							<SingleImageUpload
								label=""
								currentUrl={photoUrl || undefined}
								onUpload={setPhotoUrl}
								onRemove={() => setPhotoUrl("")}
								onUploadingChange={setIsUploading}
							/>
						</div>

						<FieldInput
							label={t("onboarding.designer.yourName")}
							value={name}
							autoComplete="name"
							onChange={(event) => setName(event.target.value)}
							placeholder={t("onboarding.designer.namePlaceholder")}
							required
						/>

						<FieldInput
							label={t("onboarding.designer.firmStudioName")}
							value={firmName}
							autoComplete="organization"
							onChange={(event) => setFirmName(event.target.value)}
							placeholder={t("onboarding.designer.firmPlaceholder")}
						/>

						<FieldSelect
							label={t("onboarding.designer.yearsOfExperience")}
							value={experienceYears || "__none__"}
							onChange={(event) => {
								const value = event.target.value;
								setExperienceYears(value === "__none__" ? "" : value);
							}}
							options={[
								{
									value: "__none__",
									label: t("onboarding.designer.selectExperience"),
								},
								{ value: "1", label: t("onboarding.designer.exp1to2") },
								{ value: "3", label: t("onboarding.designer.exp3to5") },
								{ value: "6", label: t("onboarding.designer.exp6to10") },
								{ value: "11", label: t("onboarding.designer.exp10to15") },
								{ value: "16", label: t("onboarding.designer.exp15plus") },
							]}
						/>

						<LocationFields
							label={t("onboarding.designer.location")}
							value={location}
							onChange={setLocation}
						/>

						<CharCountTextarea
							label={t("onboarding.designer.aboutYou")}
							value={bio}
							onChange={setBio}
							maxLength={DESIGNER_BIO_MAX_LENGTH}
							placeholder={t("onboarding.designer.aboutYouPlaceholder")}
							rows={3}
						/>

						{backToRole ? (
							<div className="flex gap-3" style={{ marginTop: 24 }}>
								{backToRole}
								<StudioButton
									type="submit"
									variant="primary"
									disabled={isUploading}
									style={{ flex: 1 }}
								>
									{t("common.continue")}
								</StudioButton>
							</div>
						) : (
							<StudioButton
								type="submit"
								variant="primary"
								disabled={isUploading}
								style={{ width: "100%", marginTop: 24 }}
							>
								{t("common.continue")}
							</StudioButton>
						)}
					</form>
				</FloatPanel>
			)}

			{step === "expertise" && (
				<FloatPanel elevation={2} padding={PANEL_PADDING}>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.designer.expertiseStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.designer.expertiseStepSubtitle")}
					</p>

					<form onSubmit={handleExpertiseSubmit} className="space-y-7">
						<CityChipsPicker
							label={t("onboarding.designer.citiesYouServe")}
							cities={cityOptions}
							selected={cities}
							onChange={setCities}
							addPlaceholder={t("onboarding.designer.addCityPlaceholder")}
						/>

						<div>
							<OptionChips
								label={t("onboarding.designer.designStyles")}
								options={designStyleOptions(t, styles)}
								selected={styles}
								onChange={setStyles}
							/>
							<div className="mt-3 flex gap-2">
								<input
									type="text"
									value={customStyle}
									maxLength={CUSTOM_STYLE_MAX_LENGTH}
									aria-label={t("onboarding.designer.addStylePlaceholder")}
									onChange={(event) => setCustomStyle(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.preventDefault();
											addCustomStyle();
										}
									}}
									placeholder={t("onboarding.designer.addStylePlaceholder")}
									className="h-10 min-w-0 flex-1 rounded-[7px] border border-dashboard-border bg-dashboard-warm-450 px-3.5 font-body text-sm text-dashboard-text outline-none transition-all duration-200 focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)]"
								/>
								<StudioButton
									type="button"
									variant="secondary"
									size="sm"
									onClick={addCustomStyle}
									disabled={!customStyle.trim()}
								>
									{t("onboarding.fields.addCustom")}
								</StudioButton>
							</div>
						</div>

						<OptionChips
							label={t("onboarding.designer.servicesYouOffer")}
							options={designerServiceOptions(t, services)}
							selected={services}
							onChange={setServices}
						/>

						<OptionChips
							label={t("onboarding.designer.projectTypes")}
							options={designerProjectTypeOptions(t)}
							selected={projectTypes}
							onChange={(next) =>
								setProjectTypes(next as DesignerProjectType[])
							}
							otherValue="other"
							otherText={projectTypesOther}
							onOtherTextChange={setProjectTypesOther}
						/>

						<BudgetRangeFields
							label={t("onboarding.designer.budgetQuestion")}
							value={budget}
							onChange={setBudget}
						/>

						<div className="flex gap-3 pt-2">
							<StudioButton
								type="button"
								onClick={() => setStep("basic")}
								variant="secondary"
								style={{ flex: 1 }}
							>
								{t("common.back")}
							</StudioButton>
							<StudioButton type="submit" variant="primary" style={{ flex: 1 }}>
								{t("common.continue")}
							</StudioButton>
						</div>
					</form>
				</FloatPanel>
			)}

			{step === "showcase" && (
				<FloatPanel elevation={2} padding={PANEL_PADDING}>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.designer.showcaseStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.designer.showcaseStepSubtitle")}
					</p>

					<div className="mb-2">
						<FieldGroupLabel>
							{t("onboarding.designer.showcaseImages")}
						</FieldGroupLabel>
						<MultiImageUpload
							label=""
							currentUrls={portfolioImages}
							maxImages={10}
							onUploadingChange={setIsUploading}
							onUpload={(urls) =>
								setPortfolioImages((prev) => [...prev, ...urls])
							}
							onRemove={(index) =>
								setPortfolioImages((prev) => prev.filter((_, i) => i !== index))
							}
						/>
					</div>
					<p className="font-body text-[13px] text-dashboard-muted">
						{t("onboarding.designer.showcaseLaterHint")}
					</p>

					<div className="flex flex-col gap-3 pt-6 sm:flex-row">
						<StudioButton
							type="button"
							onClick={() => setStep("expertise")}
							variant="secondary"
							className="w-full sm:w-auto sm:flex-1"
						>
							{t("common.back")}
						</StudioButton>
						<StudioButton
							type="button"
							variant="secondary"
							disabled={isLoading || isUploading}
							onClick={() => submit([])}
							className="w-full sm:w-auto sm:flex-1"
						>
							{t("common.skipForNow")}
						</StudioButton>
						<StudioButton
							type="button"
							variant="primary"
							disabled={isLoading || isUploading}
							onClick={() => submit(portfolioImages)}
							className="w-full sm:w-auto sm:flex-1"
						>
							{isLoading
								? t("common.creating")
								: t("onboarding.designer.createProfile")}
						</StudioButton>
					</div>
				</FloatPanel>
			)}
		</>
	);
}
